"use client";

import { Suspense, lazy, type ComponentType } from "react";

import type { ChartCandle } from "@/app/api/live/chart/route";
import { hasDedicatedChart, type DedicatedChartSlug } from "@/components/charts/chartSlugs";
import ChartPanelFallback from "@/components/pages/charts/ChartPanelFallback";

function withChartSuspense<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>,
): ComponentType<P> {
  const LazyComponent = lazy(factory);

  function SuspendedChart(props: P) {
    return (
      <Suspense fallback={<ChartPanelFallback />}>
        <LazyComponent {...props} />
      </Suspense>
    );
  }

  return SuspendedChart;
}

type PriceChartProps = {
  variant?: "default" | "shielded";
  title?: string;
  dropdownLabel?: string;
  dropdownOptions?: string[];
  legendItems?: { label: string; value: string; color: string }[];
  className?: string;
  initialChartCandles?: ChartCandle[];
};

export const LazyPriceChart = withChartSuspense<PriceChartProps>(() =>
  import("@/components/pages/home/PriceChart/PriceChart"),
);

const LazyZecVsBtcChart = withChartSuspense(() =>
  import("@/components/pages/charts/zec-vs-btc/ZecVsBtcChart"),
);
const LazyZecVsEthChart = withChartSuspense(() =>
  import("@/components/pages/charts/zec-vs-eth/ZecVsEthChart"),
);
const LazyZecVsXmrChart = withChartSuspense(() =>
  import("@/components/pages/charts/zec-vs-xmr/ZecVsXmrChart"),
);
const LazyZecVsGoldChart = withChartSuspense(() =>
  import("@/components/pages/charts/zec-vs-gold/ZecVsGoldChart"),
);
const LazyZecVsSp500Chart = withChartSuspense(() =>
  import("@/components/pages/charts/zec-vs-sp500/ZecVsSp500Chart"),
);
const LazyZecVsUsdChart = withChartSuspense(() =>
  import("@/components/pages/charts/zec-vs-usd/ZecVsUsdChart"),
);
const LazyShieldedZcashChart = withChartSuspense(() =>
  import("@/components/pages/charts/shielded-pools/ShieldedZcashChart"),
);

const LAZY_CHART_COMPONENT_MAP = {
  "zec-vs-btc": LazyZecVsBtcChart,
  "zec-vs-eth": LazyZecVsEthChart,
  "zec-vs-xmr": LazyZecVsXmrChart,
  "zec-vs-gold": LazyZecVsGoldChart,
  "zec-vs-sp500": LazyZecVsSp500Chart,
  "zec-vs-usd": LazyZecVsUsdChart,
  "shielded-zcash": LazyShieldedZcashChart,
} satisfies Record<DedicatedChartSlug, ComponentType>;

export function LazyChartBySlug({ slug }: { slug: string }): React.ReactElement {
  if (hasDedicatedChart(slug)) {
    const Chart = LAZY_CHART_COMPONENT_MAP[slug];
    return <Chart />;
  }
  return <LazyPriceChart variant="shielded" />;
}
